import React, { useState, useRef, useEffect } from "react";
import { API_BASE_URL } from "../../config";
import axios from "axios";
import useIsMountedRef from "../../utils/mountedRef";
import { useHistory, useLocation } from "react-router-dom";
import LoyiaInlineIcon from "../../views/loyia/LoyiaInlineIcon";
import logoBleu from "../../assets/images/logo_bleu.png";
import "./SectionNavigation.css";
import { OTHERS } from "../../views/Home";
import { DMTG } from "../../views/Home";
import { DEACTIVATED } from "../../views/Home";

const getActiveSectionFromPath = (pathname) => {
  if (pathname === "/" || pathname === "/accueil") {
    return "accueil";
  }

  if (
    pathname.startsWith("/avis") ||
    pathname.startsWith("/pratique")
  ) {
    return "expertise";
  }

  if (pathname.startsWith("/tarif_des_notaires") || pathname.startsWith("/fiches")) {
    return "outils";
  }

  if (
    pathname === "/categorie/autres" ||
    pathname.includes("/others/") ||
    pathname.includes("/dmtg/")
  ) {
    return "calculateurs";
  }

  if (pathname.startsWith("/categorie") || pathname.startsWith("/AnalysePatrimoniale")) {
    return "simulateurs";
  }

  return null;
};

const isDropdownTargetActive = (location, targetUrl) => {
  if (!targetUrl) {
    return false;
  }

  const [pathAndQuery, targetHash = ""] = targetUrl.split("#");
  const [targetPath, targetQuery = ""] = pathAndQuery.split("?");

  const pathMatches =
    location.pathname === targetPath ||
    location.pathname.startsWith(`${targetPath}/`);

  if (!pathMatches) {
    return false;
  }

  if (targetQuery) {
    const currentParams = new URLSearchParams(location.search);
    const targetParams = new URLSearchParams(targetQuery);
    const queryMatches = Array.from(targetParams.entries()).every(
      ([key, value]) => currentParams.get(key) === value
    );

    if (!queryMatches) {
      return false;
    }
  }

  if (targetHash) {
    return location.hash === `#${targetHash}`;
  }

  return true;
};

const SectionNavigation = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesOther, setCategoriesOther] = useState([]);
  const [patrimoineSubcategories, setPatrimoineSubcategories] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const navbarRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const isMountedRef = useIsMountedRef();
  const others = OTHERS;
  const dmtg = DMTG;
  const deactivated = DEACTIVATED;

  // Vérifier si l'utilisateur est connecté
  let isAuthenticated = false;
  try {
    isAuthenticated = Boolean(JSON.parse(localStorage.getItem("user"))?.jwt);
  } catch {
    // Une session illisible doit être traitée comme une visite anonyme.
  }
  const activeSection = getActiveSectionFromPath(location.pathname);

  useEffect(() => {
    // Ne faire les appels API que si l'utilisateur est connecté
    if (!isAuthenticated) {
      return;
    }

    // Récupération des catégories
    const categoriesRequest = {
      method: "get",
      url: `${API_BASE_URL}/categories`,
      headers: {
        "Content-Type": "application/json",
        Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
      },
    };
    axios(categoriesRequest)
      .then((response) => {
        if (response.status === 200 && response.data != null) {
          if (isMountedRef.current) {
            setCategories(response.data);
          }
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("user");
          if (error.response.data?.error === "Other_user_connected") {
            localStorage.setItem("other_user_connected", "other_user_connected");
          } else {
            sessionStorage.setItem("session_expired", "true");
          }
          window.location.replace("/login");
        }
      });

    // Récupération des catégories autres
    const othersRequest = {
      method: "get",
      url: `${API_BASE_URL}/categories/autres`,
      headers: {
        "Content-Type": "application/json",
        Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
      },
    };
    axios(othersRequest)
      .then((response) => {
        if (response.status === 200 && response.data != null) {
          if (isMountedRef.current) {
            setCategoriesOther(response.data.subcategories);
          }
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("user");
          if (error.response.data?.error === "Other_user_connected") {
            localStorage.setItem("other_user_connected", "other_user_connected");
          } else {
            sessionStorage.setItem("session_expired", "true");
          }
          window.location.replace("/login");
        }
      });

    const patrimoineRequest = {
      method: "get",
      url: `${API_BASE_URL}/categories/patrimoine`,
      headers: {
        "Content-Type": "application/json",
        Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
      },
    };
    axios(patrimoineRequest)
      .then((response) => {
        if (response.status === 200 && response.data != null) {
          if (isMountedRef.current) {
            setPatrimoineSubcategories(response.data.subcategories || []);
          }
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("user");
          if (error.response.data?.error === "Other_user_connected") {
            localStorage.setItem("other_user_connected", "other_user_connected");
          } else {
            sessionStorage.setItem("session_expired", "true");
          }
          window.location.replace("/login");
        }
      });
  }, [isAuthenticated, isMountedRef]);

  const simulateursDeFraisMenu = [
    ...categories
      .filter((category) => category.url_name !== "autres")
      .map((category) => ({
        key: category.url_name,
        label: category.categorie_name,
        url: `/categorie/${category.url_name}`,
      })),
  ];

  const getSimulateurPath = (simulateur) => {
    if (!simulateur?.id) {
      return "/";
    }

    if (dmtg.includes(simulateur.id)) {
      return `/categorie/autres/dmtg/${simulateur.id}`;
    }

    if (others.includes(simulateur.id)) {
      const categoryUrl = simulateur.id >= 113 ? "patrimoine" : "autres";
      return `/categorie/${categoryUrl}/others/${simulateur.id}`;
    }

    const categoryUrl = simulateur.subcategorie?.[0]?.categorie?.url_name || "categorie";
    return `/categorie/${categoryUrl}/simulateur/${simulateur.id}`;
  };

  const mergedCalculateurSources = [...categoriesOther, ...patrimoineSubcategories];

  const calculateursDiversMenu = [
    ...(mergedCalculateurSources[0]?.simulateur
      ?.filter((item) => !deactivated.includes(item.id))
      .map((item) => ({
        key: `autres-${item.id}`,
        label: item.name,
        url: getSimulateurPath(item),
      })) || []),
    ...(mergedCalculateurSources[2]?.simulateur
      ?.filter((item) => !deactivated.includes(item.id))
      .map((item) => ({
        key: `patrimoine-${item.id}`,
        label: item.name,
        url: getSimulateurPath(item),
      })) || []),
  ];

  const closeAllDropdowns = () => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const navigateTo = (path) => {
    closeAllDropdowns();
    history.push(path);
  };

  const getDropdownLinkClassName = (targetUrl) =>
    isDropdownTargetActive(location, targetUrl) ? "is-current" : "";

  const setActiveSectionAndClose = (sectionId) => {
    // Navigation vers les pages correspondantes
    switch(sectionId) {
      case 'accueil':
        navigateTo('/');
        break;
      case 'simulateurs':
        navigateTo(`/categorie/${categories.find((value) => value.categorie_id !== 5)?.url_name || "immobilier"}`);
        break;
      case 'calculateurs':
        navigateTo('/categorie/autres');
        break;
      case 'outils':
        navigateTo('/tarif_des_notaires');
        break;
      case 'expertise':
        navigateTo('/avis');
        break;
      default:
        break;
    }
  };

  const isDesktopHoverNavigation = () =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1201px)").matches;

  const handleSectionNavigationClick = (sectionId) => {
    if (sectionId === "accueil") {
      setActiveSectionAndClose(sectionId);
      return;
    }

    // Pour tous les autres menus, juste ouvrir/fermer le dropdown sans navigation
    setOpenDropdown((currentDropdown) => (currentDropdown === sectionId ? null : sectionId));
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((currentValue) => !currentValue);
  };

  const handleMouseEnter = (itemId) => {
    if (!isDesktopHoverNavigation()) {
      return;
    }

    if (itemId === "accueil") {
      closeAllDropdowns();
      return;
    }

    setOpenDropdown(itemId);
  };

  const handleMouseLeave = () => {
    if (!isDesktopHoverNavigation()) {
      return;
    }

    closeAllDropdowns();
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        closeAllDropdowns();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    closeAllDropdowns();
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <nav
      className={`navbar ${isScrolled ? "navbar--scrolled" : ""} ${isMobileMenuOpen ? "navbar--mobile-open" : ""}`}
      id="navbar"
      ref={navbarRef}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="section-nav-menu-button"
        aria-label={isMobileMenuOpen ? "Fermer les accès rapides" : "Ouvrir les accès rapides"}
        aria-expanded={isMobileMenuOpen}
        aria-controls="section-navigation-list"
        onClick={toggleMobileMenu}
      >
        <span className="section-nav-menu-icon" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        <span className="section-nav-menu-copy">
          <span className="section-nav-menu-title">Accès rapide</span>
        </span>
      </button>
      <button
        type="button"
        className="section-nav-backdrop"
        aria-label="Fermer le menu"
        tabIndex={isMobileMenuOpen ? 0 : -1}
        onClick={closeAllDropdowns}
      />
      <button
        type="button"
        className="section-nav-logo-button"
        aria-label="Retour à l'accueil"
        tabIndex={isScrolled ? 0 : -1}
        onClick={() => handleSectionNavigationClick('accueil')}
      >
        <img src={logoBleu} alt="Éditions Langloÿs" />
      </button>
      <ul
        className="nav-inner section-nav-list"
        id="section-navigation-list"
        aria-label="Navigation des sections"
      >
        <li className="section-nav-drawer-header">
          <div className="section-nav-drawer-title">
            <span>Accès rapide</span>
          </div>
          <button type="button" className="section-nav-close-button" onClick={closeAllDropdowns}>
            Fermer
          </button>
        </li>

        {/* Accueil */}
        <li
          className="nav-item" 
          id="item-accueil"
          onMouseEnter={() => handleMouseEnter('accueil')}
        >
          <a
            className={`nav-link ${activeSection === 'accueil' ? 'is-active' : ''}`}
            data-section="accueil"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              handleSectionNavigationClick('accueil');
            }}
          >
            <span className="section-nav-link-label">Accueil</span>
          </a>
        </li>

        {/* Simulateurs de frais */}
        <li
          className={`nav-item ${openDropdown === 'simulateurs' ? 'is-open' : ''}`} 
          id="item-simulateurs"
          onMouseEnter={() => handleMouseEnter('simulateurs')}
          onFocus={() => handleMouseEnter('simulateurs')}
        >
          <button
            type="button"
            className={`nav-link ${activeSection === 'simulateurs' ? 'is-active' : ''}`}
            data-section="simulateurs"
            onClick={() => handleSectionNavigationClick('simulateurs')}
          >
            <span className="section-nav-link-label">Simulateurs de frais</span>
            <span className="section-nav-link-trailing" aria-hidden="true">
              <span className="chevron">▾</span>
            </span>
          </button>
          <div className="dropdown dropdown--simulateurs">
            <ul>
              {simulateursDeFraisMenu.map((item) => (
                <li key={item.key}>
                  <a
                    className={getDropdownLinkClassName(item.url)}
                    href={item.url}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateTo(item.url);
                    }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </li>

        {/* Calculateurs divers */}
        <li
          className={`nav-item ${openDropdown === 'calculateurs' ? 'is-open' : ''}`} 
          id="item-calculateurs"
          onMouseEnter={() => handleMouseEnter('calculateurs')}
          onFocus={() => handleMouseEnter('calculateurs')}
        >
          <button
            type="button"
            className={`nav-link ${activeSection === 'calculateurs' ? 'is-active' : ''}`}
            data-section="calculateurs"
            onClick={() => handleSectionNavigationClick('calculateurs')}
          >
            <span className="section-nav-link-label">Calculateurs divers</span>
            <span className="section-nav-link-trailing" aria-hidden="true">
              <span className="chevron">▾</span>
            </span>
          </button>
          <div className="dropdown dropdown--calculateurs">
            <ul>
              {calculateursDiversMenu.map((item) => (
                <li key={item.key}>
                  <a
                    className={getDropdownLinkClassName(item.url)}
                    href={item.url}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateTo(item.url);
                    }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </li>

        {/* La boîte à outils */}
        <li
          className={`nav-item ${openDropdown === 'outils' ? 'is-open' : ''}`} 
          id="item-outils"
          onMouseEnter={() => handleMouseEnter('outils')}
          onFocus={() => handleMouseEnter('outils')}
        >
          <button
            type="button"
            className={`nav-link ${activeSection === 'outils' ? 'is-active' : ''}`}
            data-section="outils"
            onClick={() => handleSectionNavigationClick('outils')}
          >
            <span className="section-nav-link-label">La boîte à outils</span>
            <span className="section-nav-link-trailing" aria-hidden="true">
              <span className="chevron">▾</span>
            </span>
          </button>
          <div className="dropdown dropdown--outils">
            <ul>
              <li>
                <a className={getDropdownLinkClassName('/tarif_des_notaires')} href="/tarif_des_notaires" onClick={(e) => { e.preventDefault(); navigateTo('/tarif_des_notaires'); }}>
                  Le tarif des notaires et la fiscalité des actes
                </a>
              </li>
              <li>
                <a className={getDropdownLinkClassName('/fiches')} href="/fiches" onClick={(e) => { e.preventDefault(); navigateTo('/fiches'); }}>
                  Les fiches pratiques
                </a>
              </li>
              <li className="dropdown-item--disabled" aria-disabled="true">
                <span className="dropdown-disabled-link dropdown-inline-label">
                  <span>Actes d'Affaires : formalités, démarches préalables et droits d'enregistrements</span>
                  <span className="dropdown-inline-badge-slot">
                    <span className="dropdown-coming-soon-badge">À venir</span>
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </li>

        {/* Expertise & Pratique */}
        <li
          className={`nav-item ${openDropdown === 'expertise' ? 'is-open' : ''}`} 
          id="item-expertise"
          onMouseEnter={() => handleMouseEnter('expertise')}
          onFocus={() => handleMouseEnter('expertise')}
        >
          <button
            type="button"
            className={`nav-link ${activeSection === 'expertise' ? 'is-active' : ''}`}
            data-section="expertise"
            onClick={() => handleSectionNavigationClick('expertise')}
          >
            <span className="section-nav-link-label">Expertise &amp; Pratique</span>
            <span className="section-nav-link-trailing" aria-hidden="true">
              <span className="chevron">▾</span>
            </span>
          </button>
          <div className="dropdown dropdown--expertise">
            <ul>
              <li>
                <a className={getDropdownLinkClassName('/avis')} href="/avis" onClick={(e) => { e.preventDefault(); navigateTo('/avis'); }}>
                  Le Coin des Experts
                </a>
              </li>
              <li className="dropdown-item--disabled" aria-disabled="true">
                <span className="dropdown-disabled-link dropdown-inline-label" aria-disabled="true">
                  <span>La Méthode Taxe et Actes® issue des Pratiques I et II</span>
                  <span className="dropdown-inline-badge-slot">
                    <span className="dropdown-coming-soon-badge">À venir</span>
                  </span>
                </span>
              </li>
              <li>
                <a className={getDropdownLinkClassName('/loyia')} href="/loyia" onClick={(e) => { e.preventDefault(); navigateTo('/loyia'); }}>
                  <span className="nav-loyia-inline-word">LOYIA</span>
                  <LoyiaInlineIcon size={24} className="nav-loyia-inline-icon" />
                  <span className="nav-loyia-inline-text">
                    , l'Intelligence au service de votre métier : posez-moi une question !
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </nav>
  );
};

export default SectionNavigation;
